import { useState, useEffect, useCallback, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { isSubmissionRateLimited, registerSubmission } from "@/utils/formGuard";

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedService?: string;
}

const submitAddr = "https://readdy.ai/api/form/d9os870senbnioleskrg";

const serviceOptions = [
  "Consultoria",
  "Consultoria de Seguridad",
  "CyberSOC",
  "Service Desk",
  "Datacenter e Infraestructura",
  "Ingenieria de Productos",
  "Secure Academy",
  "Data Security",
  "Endpoint Security",
  "Cloud Security",
  "Application Security",
  "Networking Security",
  "Ciberinteligencia",
  "Otro",
];

export default function QuoteModal({ isOpen, onClose, preselectedService }: QuoteModalProps) {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormStatus("idle");
      setFormError("");
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setVisible(false);
        setTimeout(onClose, 200);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setFormStatus("submitting");
      setFormError("");

      const form = e.currentTarget;
      const formData = new FormData(form);

      const honeypot = formData.get("company_alt")?.toString().trim() || "";
      if (honeypot) {
        setFormStatus("success");
        form.reset();
        return;
      }

      if (isSubmissionRateLimited()) {
        setFormStatus("error");
        setFormError("Demasiados intentos seguidos. Espera un minuto e intenta de nuevo.");
        return;
      }

      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== "company_alt") {
          payload.append(key, value.toString());
        }
      });

      try {
        const response = await fetch(submitAddr, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: payload.toString(),
        });

        const responseText = await response.text();
        let parsed: Record<string, unknown> | null = null;
        try {
          parsed = JSON.parse(responseText);
        } catch {
          parsed = null;
        }

        const serverMsg =
          (parsed?.meta as Record<string, string>)?.message ||
          (parsed?.meta as Record<string, string>)?.detail ||
          (parsed?.message as string) ||
          responseText;

        const isSpam =
          typeof serverMsg === "string" &&
          (serverMsg.toLowerCase().includes("spam") ||
            serverMsg.toLowerCase().includes("form data is spam"));

        if (!response.ok || isSpam || (parsed && (parsed.code as string) !== "OK")) {
          setFormStatus("error");
          setFormError(serverMsg || "Error al enviar. Por favor intenta de nuevo.");
          return;
        }

        setFormStatus("success");
        registerSubmission();
        form.reset();
      } catch {
        setFormStatus("error");
        setFormError("Error de conexion. Intenta mas tarde.");
      }
    },
    []
  );

  if (!isOpen) return null;

  const serviceOptionsResolved =
    preselectedService && !serviceOptions.includes(preselectedService)
      ? [...serviceOptions, preselectedService]
      : serviceOptions;

  const inputBase =
    "w-full px-4 py-3 rounded-xl bg-white border border-[#D0D8E8] text-[#1A2B4A] text-sm placeholder:text-[#A8B4C4] focus:outline-none focus:ring-2 focus:ring-[#4A7DBF]/20 focus:border-[#4A7DBF]/40 transition-all";
  const labelBase =
    "block text-[11px] font-label font-semibold text-[#5A6B8A] mb-1.5 uppercase tracking-wider";

  return (
    <div
      className={`fixed inset-0 z-[90] flex items-center justify-center p-4 transition-all duration-200 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        className="absolute inset-0 bg-[#1A2B4A]/60 backdrop-blur-sm transition-opacity duration-200"
        onClick={handleClose}
        role="presentation"
      />

      <div
        className={`relative w-full max-w-xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-[#E8EDF5] transition-all duration-300 ${
          visible ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
        }`}
      >
        {/* Header */}
        <div className="relative">
          <div className="h-28 bg-gradient-to-br from-[#4A7DBF] to-[#1A3A5C] overflow-hidden rounded-t-2xl">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)", backgroundSize: "24px 24px" }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 mb-2">
                  <i className="ri-chat-smile-3-line text-xl text-white" />
                </div>
                <h3 className="font-heading font-bold text-lg text-white">Habla con un experto</h3>
                <p className="text-[11px] text-white/60 font-label mt-0.5">Te respondemos en menos de 15 minutos</p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm border border-white/30 text-[#5A6B8A] hover:text-[#1A2B4A] shadow-sm transition-all duration-200 cursor-pointer"
            aria-label="Cerrar"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {formStatus === "success" ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-emerald-50 border border-emerald-200 mb-4">
                <i className="ri-check-line text-2xl text-emerald-500" />
              </div>
              <h4 className="font-heading font-semibold text-lg text-[#1A2B4A] mb-2">
                Mensaje enviado!
              </h4>
              <p className="text-sm text-[#7A8BA8] mb-5 max-w-xs mx-auto">
                Un especialista de Openlab Secure te contactara pronto.
              </p>
              <button
                type="button"
                onClick={() => {
                  setFormStatus("idle");
                  handleClose();
                }}
                className="inline-flex items-center px-5 py-2.5 rounded-full bg-[#4A7DBF]/10 border border-[#4A7DBF]/20 text-[#4A7DBF] font-label font-semibold text-sm hover:bg-[#4A7DBF]/15 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-line mr-1.5" />
                Entendido
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
              {/* Nombres y Apellidos */}
              <div>
                <label htmlFor="quote-fullname" className={labelBase}>
                  Nombres y apellidos <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="quote-fullname"
                  name="fullname"
                  required
                  className={inputBase}
                  placeholder="Ej. Juan Carlos Rodriguez Lopez"
                />
              </div>

              {/* Telefono + Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="quote-telefono" className={labelBase}>
                    Telefono (celular) <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="tel"
                    id="quote-telefono"
                    name="telefono"
                    required
                    className={inputBase}
                    placeholder="+51 999 999 999"
                  />
                </div>
                <div>
                  <label htmlFor="quote-email" className={labelBase}>
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    id="quote-email"
                    name="email"
                    required
                    className={inputBase}
                    placeholder="tu@empresa.com"
                  />
                </div>
              </div>

              {/* RUC + Empresa */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="quote-ruc" className={labelBase}>
                    RUC
                  </label>
                  <input
                    type="text"
                    id="quote-ruc"
                    name="ruc"
                    maxLength={20}
                    className={inputBase}
                    placeholder="Ej. 20548796321"
                  />
                </div>
                <div>
                  <label htmlFor="quote-empresa" className={labelBase}>
                    Empresa
                  </label>
                  <input
                    type="text"
                    id="quote-empresa"
                    name="empresa"
                    className={inputBase}
                    placeholder="Nombre de tu empresa"
                  />
                </div>
              </div>

              {/* Servicio */}
              <div>
                <label htmlFor="quote-servicio" className={labelBase}>
                  Servicio de interes
                </label>
                <select
                  id="quote-servicio"
                  name="servicio"
                  className={`${inputBase} appearance-none cursor-pointer`}
                  defaultValue={preselectedService || ""}
                >
                  <option value="" disabled>Selecciona un servicio</option>
                  {serviceOptionsResolved.map((svc) => (
                    <option key={svc} value={svc}>{svc}</option>
                  ))}
                </select>
              </div>

              {/* Hidden field: source slide info for lead tracking */}
              {preselectedService && (
                <input type="hidden" name="source_slide" value={preselectedService} />
              )}

              {/* Honeypot */}
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              {formError && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs">
                  <i className="ri-error-warning-line flex-shrink-0" />
                  <span>{formError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === "submitting"}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-[#4A7DBF] to-[#3A6DAF] text-white font-label font-semibold text-sm hover:from-[#3A6DAF] hover:to-[#2A5D9F] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md whitespace-nowrap"
              >
                {formStatus === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    Hablar con un experto
                    <i className="ri-send-plane-line" />
                  </>
                )}
              </button>

              <p className="text-[10px] text-[#A8B4C4] text-center leading-relaxed">
                Al enviar, aceptas nuestra{" "}
                <Link to="/politica-privacidad" className="text-[#4A7DBF] hover:underline cursor-pointer">
                  Política de Privacidad
                </Link>{" "}
                y los{" "}
                <Link to="/terminos" className="text-[#4A7DBF] hover:underline cursor-pointer">
                  Términos y Condiciones
                </Link>
                .
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}